import { useRef } from 'react'
import { Icon } from '#app/components/ui/icon.tsx'

type AudioUploadDropzoneProps = {
	onUpload: (file: File) => void | Promise<void>
	progress: number
	uploading: boolean
	error: string | null
	accept?: string
	statusMessage?: string | null
	/** When true, show tip to keep tab open (large / slow uploads). */
	largeUploadWarning?: boolean
}

export function AudioUploadDropzone({
	onUpload,
	progress,
	uploading,
	error,
	accept = 'audio/*,.wav,.m4a,.mp3',
	statusMessage,
	largeUploadWarning = false,
}: AudioUploadDropzoneProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)

	function handleFile(file: File) {
		void onUpload(file)
	}

	return (
		<div className="space-y-4">
			<label
				htmlFor={uploading ? undefined : 'upload-audio'}
				className="block"
			>
				<div
					className={`border-base-content/30 bg-base-200 animate-pulse-shadow hover:bg-base-200 mx-auto my-4 flex aspect-video w-full max-w-xl flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed transition-transform duration-200 ${
						uploading
							? 'cursor-wait'
							: 'cursor-pointer hover:scale-[1.02]'
					}`}
					onDragOver={(e) => e.preventDefault()}
					onDrop={(e) => {
						e.preventDefault()
						if (uploading) return
						const file = e.dataTransfer.files[0]
						if (file) handleFile(file)
					}}
				>
					{uploading ? (
						<>
							<div className="loading size-10" />
							<div className="flex w-full max-w-md flex-col items-center gap-2 px-4 text-center">
								<progress
									className="progress progress-primary h-3 w-full"
									value={progress}
									max={100}
								/>
								<span className="text-sm leading-snug">
									{statusMessage || 'Working on your file…'}
								</span>
								{largeUploadWarning ? (
									<p className="text-base-content/70 text-xs leading-snug">
										Large file — this can take a while. Keep this tab in the
										foreground; background tabs may upload slower.
									</p>
								) : null}
							</div>
						</>
					) : (
						<>
							<span className="text-base">Drop a file here, or click to browse</span>
							<Icon name="upload" className="size-10 text-base" />
						</>
					)}
				</div>
			</label>
			<input
				id="upload-audio"
				ref={fileInputRef}
				hidden
				type="file"
				accept={accept}
				disabled={uploading}
				onChange={(e) => {
					const file = e.target.files?.[0]
					if (file) handleFile(file)
					e.target.value = ''
				}}
			/>
			<p className="text-base-content/70 text-sm leading-snug">
				Very long WAV podcasts can be hundreds of MB. If export size matters,
				try <strong>FLAC</strong> or a compressed format from your editor — we
				normalize to a standard master after upload.
			</p>
			{error && (
				<p className="text-destructive text-sm" role="alert">
					{error}
				</p>
			)}
		</div>
	)
}
